import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import type { Climb } from '../../types/climb';

/** One sector of the wall, as the strip under the bar names it. */
export interface ProgressZone {
  id: string;
  name: string;
}

interface ClimbProgressBarProps {
  climbs: Climb[];
  climbType: 'boulders' | 'routes';
  activeGrade?: string | null;
  onGradeClick?: (grade: string) => void;
  /**
   * The sectors to offer as a strip of tabs under the bar. Empty means the
   * caller does not want it shown — see `showZoneStrip` in ClimbsList, which is
   * where the rule about when it earns its place lives.
   */
  zones?: ProgressZone[];
  /** The sector id being shown, or 'all'. */
  activeZone?: string;
  onZoneChange?: (zone: string) => void;
  /** Rendered on the right of the header (e.g. the "open filters" button). */
  headerAction?: ReactNode;
}

interface GradeBucket {
  label: string;
  sortKey: number;
  total: number;
  topped: number;
}

// One tab of the sector strip, worn like the section tabs above the list: they
// do the same job a step further in, so they read as the same control.
const zoneTabClass =
  'flex-1 shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm';

// Hue goes from 120 (green, easiest) to 0 (red, hardest)
function colorForIndex(i: number, n: number) {
  const hue = n <= 1 ? 60 : 120 - (i / (n - 1)) * 120;
  return `hsl(${hue}, 80%, 45%)`;
}

export default function ClimbProgressBar({
  climbs,
  climbType,
  activeGrade,
  onGradeClick,
  zones = [],
  activeZone = 'all',
  onZoneChange,
  headerAction,
}: ClimbProgressBarProps) {
  const { t } = useTranslation();

  const buckets = useMemo<GradeBucket[]>(() => {
    const map = new Map<string, GradeBucket>();
    for (const climb of climbs) {
      if (!climb.difficulty || climb.official_status !== 'confirmed') continue;
      const existing = map.get(climb.difficulty);
      if (existing) {
        existing.total += 1;
        if (climb.user_topped) existing.topped += 1;
      } else {
        map.set(climb.difficulty, {
          label: climb.difficulty,
          sortKey: climb.difficulty_value ?? Infinity,
          total: 1,
          topped: climb.user_topped ? 1 : 0,
        });
      }
    }
    return [...map.values()].sort((a, b) => {
      if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
      return a.label.localeCompare(b.label);
    });
  }, [climbs]);

  if (buckets.length === 0) return null;

  const totalClimbs = buckets.reduce((acc, b) => acc + b.total, 0);
  const totalTopped = buckets.reduce((acc, b) => acc + b.topped, 0);
  const n = buckets.length;

  return (
    <Card>
      <CardContent className="p-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium">
            {climbType === 'boulders' ? t('climbs.progress.titleBoulders') : t('climbs.progress.titleRoutes')}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="progress-summary">
              {t('climbs.progress.summary', { topped: totalTopped, total: totalClimbs })}
            </span>
            {headerAction}
          </div>
        </div>

        {/* Grade labels above the bar */}
        <div className="flex w-full" data-testid="grade-labels">
          {buckets.map((bucket) => (
            <div
              key={`label-${bucket.label}`}
              className={`text-[10px] text-center overflow-hidden text-ellipsis whitespace-nowrap px-0.5 transition-colors ${
                activeGrade === bucket.label ? 'text-foreground font-semibold' : 'text-muted-foreground'
              } ${onGradeClick ? 'cursor-pointer' : ''}`}
              style={{ flexGrow: bucket.total, flexBasis: 0 }}
              onClick={() => onGradeClick?.(bucket.label)}
            >
              {bucket.label}
            </div>
          ))}
        </div>

        {/* The stacked bar */}
        <div className="flex w-full h-3 rounded-sm overflow-hidden border border-border" data-testid="progress-bar">
          {buckets.map((bucket, i) => {
            const toppedPct = bucket.total > 0 ? (bucket.topped / bucket.total) * 100 : 0;
            const isActive = activeGrade === bucket.label;
            return (
              <div
                key={`seg-${bucket.label}`}
                data-testid={`segment-${bucket.label}`}
                className={`relative h-full border-r border-border last:border-r-0 transition-opacity ${
                  onGradeClick ? 'cursor-pointer' : ''
                } ${activeGrade && !isActive ? 'opacity-30' : 'bg-muted'}`}
                style={{ flexGrow: bucket.total, flexBasis: 0 }}
                title={`${bucket.label}: ${bucket.topped}/${bucket.total}`}
                onClick={() => onGradeClick?.(bucket.label)}
              >
                <div
                  data-testid={`topped-${bucket.label}`}
                  className="h-full"
                  style={{ width: `${toppedPct}%`, backgroundColor: colorForIndex(i, n) }}
                />
              </div>
            );
          })}
        </div>

        {/* The sectors, as a strip of tabs under the bar: which one you are
            standing in front of is the first cut a climber makes, and it belongs
            beside the progress it changes rather than behind the filter panel.
            Under it rather than over it, so the card still opens on what it is
            about. There is no "all" tab: pressing the sector already on drops
            it, which is the same gesture and one tab less to read (Radix hands
            a press on the active item back as an empty value). */}
        {zones.length > 0 && (
          <ToggleGroup
            type="single"
            value={activeZone}
            onValueChange={(value) => onZoneChange?.(value || 'all')}
            className="flex bg-muted rounded-lg p-1 overflow-x-auto [scrollbar-width:none]"
            data-testid="zone-strip"
          >
            {zones.map((zone) => (
              <ToggleGroupItem key={zone.id} value={zone.id} className={zoneTabClass}>
                {zone.name}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </CardContent>
    </Card>
  );
}
